/**
 * Location Service - Fetch counties and areas from public API
 */

export interface County {
  id: string;
  name: string;
  code: string;
  province: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Area {
  id: string;
  name: string;
  county_id: string;
  county_name?: string;
  area_type: string;
  postal_codes: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CountiesResponse {
  data: County[];
  count: number;
}

export interface AreasResponse {
  data: Area[];
  count: number;
}

const PUBLIC_API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'https://npm.rapidfix.ie';

/**
 * Fetch all active Irish counties from the public API
 */
export async function fetchCounties(): Promise<County[]> {
  try {
    const response = await fetch(`${PUBLIC_API_BASE_URL}/api/v1/public/counties`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch counties: ${response.statusText}`);
    }

    const result: CountiesResponse = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching counties:', error);
    throw error;
  }
}

/**
 * Fetch all active Irish areas from the public API
 */
export async function fetchAreas(countyId?: string): Promise<Area[]> {
  try {
    const url = countyId 
      ? `${PUBLIC_API_BASE_URL}/api/v1/public/areas?county_id=${countyId}`
      : `${PUBLIC_API_BASE_URL}/api/v1/public/areas`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch areas: ${response.statusText}`);
    }

    const result: AreasResponse = await response.json();
    return result.data || [];
  } catch (error) {
    console.error('Error fetching areas:', error);
    throw error;
  }
}

/**
 * Group counties by province for easier display
 */
export function groupCountiesByProvince(counties: County[]): Record<string, County[]> {
  return counties.reduce((acc, county) => {
    const province = county.province || 'Other';
    if (!acc[province]) {
      acc[province] = [];
    }
    acc[province].push(county);
    return acc;
  }, {} as Record<string, County[]>);
}

/**
 * Legacy service object for backward compatibility
 * @deprecated Use fetchCounties() and groupCountiesByProvince() directly
 */
export const locationService = {
  fetchCounties,
  getCounties: fetchCounties, // Alias for backward compatibility
  fetchAreas,
  getAreas: fetchAreas, // Alias for backward compatibility
  groupCountiesByProvince,
};
